import React from 'react';
import { defaultMessages, DefaultMessage } from './defaultMessages';
import { Message } from './models';
import { Map } from '../language/models';
import { doMsgReplacements } from './module';
import { useAppState } from '../core/redux/hooks';


/**
 * A React Hook for retrieving a message object from the app state translations.
 * Use this if you need information about the message like whether it contains HTML markup.
 * Use `useMsgText` if you just need the message string.
 *
 * @param str - translation key that is used to look up the appropriate translation in Redux.
 * @returns the message object if found, otherwise undefined
 */
export const useMsg = (str: string | number): Message | DefaultMessage | undefined => {
  const translations = useAppState(state => state.locale.translations[state.locale.lang.languageId]);

  return React.useMemo<Message>(
    () => (translations ?? {})[str] || defaultMessages[str],
    [str, translations],
  );
};


/**
 * A React Hook for reading a translated string from the app state translations
 * @param str - translation key that is used to look up the appropriate translation in Redux.
 * @param defaultMsg - allows you to specify text for when a translation isn't found (defaults to empty string)
 * @param replace - Object specifying key-values to replace in the message text
 * @returns the translation if found, otherwise the app's fallback message
 */
export const useMsgText = (
  str: string | number,
  defaultMsg: string = '',
  replace?: Map<string | number | undefined>,
): string => {
  const msg = useMsg(str);

  return React.useMemo(
    () => {
      // If we have provided a fallback message, don't let it be overridden by the app default
      const text = (!msg || (defaultMsg && (msg as DefaultMessage).isDefault)) ? defaultMsg : msg.text;

      return doMsgReplacements(text, replace);
    },
    [replace, defaultMsg, msg],
  );
};
